import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchPicker } from '@/components/SearchPicker';
import { toast } from 'sonner';
import { Plus, Trophy, X, Activity as ActIcon } from 'lucide-react';
import { format } from 'date-fns';

export function ActivityLogger({ title = 'Additional Activities', subtitle = 'Log extra activities like Padel, Golf or Swimming. These show as a positive bonus.', showBonusTag = true }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [activities, setActivities] = useState([]);
  const [logged, setLogged] = useState([]);
  const [summary, setSummary] = useState(null);
  const [picked, setPicked] = useState(null);
  const [duration, setDuration] = useState('');

  const load = async () => {
    const [a, l, s] = await Promise.all([
      api.get('/activities', { params: { enabled_only: true } }),
      api.get('/logged-activities', { params: { date: today } }),
      api.get('/logged-activities/summary', { params: { days: 7 } }),
    ]);
    setActivities(a.data); setLogged(l.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const logIt = async () => {
    if (!picked?.name?.trim()) return toast.error('Choose an activity');
    if (!duration || parseInt(duration) <= 0) return toast.error('Enter duration in minutes');
    const match = activities.find(a => a.name.toLowerCase() === picked.name.toLowerCase());
    await api.post('/logged-activities', {
      activity_id: match?.id || picked.id || null,
      name: picked.name, category: match?.category || picked.category || 'Other',
      duration_min: parseInt(duration),
    });
    toast.success(`Logged ${picked.name}`);
    setPicked(null); setDuration('');
    load();
  };

  const del = async (id) => { await api.delete(`/logged-activities/${id}`); load(); };

  return (
    <div className="regen-card-elevated p-5" data-testid="activity-logger">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-[var(--c-green-ink)]" />
        <span className="font-display font-bold">{title}</span>
        {showBonusTag && <span className="text-[10px] text-muted-foreground ml-auto">bonus · doesn't affect compliance</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>

      <div className="space-y-2.5">
        <SearchPicker items={activities} value={picked?.name || ''} onPick={setPicked} placeholder="Search activity (Padel, Golf…)" testid="activity-search" />
        <div className="flex gap-2">
          <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} placeholder="Minutes" data-testid="activity-duration" className="bg-[var(--c-film)] border-[var(--c-hair)] h-12 rounded-xl font-mono" />
          <Button onClick={logIt} data-testid="log-activity-btn" className="h-12 px-5 bg-[var(--c-green)] text-[#081726] font-semibold rounded-xl"><Plus className="w-4 h-4 mr-1" /> Log</Button>
        </div>
      </div>

      {summary && (summary.sessions > 0) && (
        <div className="grid grid-cols-2 gap-3 mt-4 text-center">
          <div className="rounded-xl bg-[var(--c-green-soft)] p-3"><div className="font-mono text-2xl text-[var(--c-green-ink)]">+{summary.sessions}</div><div className="label-mini mt-0.5">Sessions · 7d</div></div>
          <div className="rounded-xl bg-[var(--c-green-soft)] p-3"><div className="font-mono text-2xl text-[var(--c-green-ink)]">+{summary.total_minutes}</div><div className="label-mini mt-0.5">Minutes · 7d</div></div>
        </div>
      )}

      {logged.length > 0 && (
        <div className="mt-4">
          <div className="label-mini mb-2">Today</div>
          <div className="space-y-2">
            {logged.map(l => (
              <div key={l.id} className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3" data-testid={`logged-activity-${l.id}`}>
                <ActIcon className="w-4 h-4 text-[var(--c-green-ink)]" />
                <div className="flex-1"><div className="text-sm font-medium">{l.name}</div><div className="text-[11px] text-muted-foreground">{l.category}</div></div>
                <span className="font-mono text-sm text-[var(--c-green-ink)]">{l.duration_min} min</span>
                <button onClick={() => del(l.id)} data-testid={`del-activity-${l.id}`} className="text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
